import type { CasinoGame, TaskResolvedGame } from "../../types/gamePass";

/** Coerce catalogue / rule ids to a stable string key (`901` vs `"901"`). */
export const normalizeCasinoGameId = (id: unknown): string =>
  String(id ?? "").trim();

/** Every non-empty id alias on a raw casino-games API row. */
export const collectCasinoGameMatchIdsFromApi = (raw: unknown): string[] => {
  if (!raw || typeof raw !== "object") return [];
  const row = raw as Record<string, unknown>;
  const ids = [row.game_id, row.id, row.display_game_id]
    .map(normalizeCasinoGameId)
    .filter(Boolean);
  return Array.from(new Set(ids));
};

/** All ids a normalized catalogue row can match against a saved task rule. */
export const getCasinoGameMatchIds = (
  game: Pick<CasinoGame, "game_id" | "match_ids">,
): string[] => {
  const ids = [...(game.match_ids ?? []), normalizeCasinoGameId(game.game_id)]
    .map(normalizeCasinoGameId)
    .filter(Boolean);
  return Array.from(new Set(ids));
};

/** Whether a saved selection includes this catalogue row (any id alias). */
export const isCasinoGameIdSelected = (
  selectedIds: ReadonlyArray<string>,
  game: Pick<CasinoGame, "game_id" | "match_ids">,
): boolean => {
  const selected = new Set(selectedIds.map(normalizeCasinoGameId));
  return getCasinoGameMatchIds(game).some((id) => selected.has(id));
};

/** The id already present on the task draft for this row, if any. */
export const resolveSelectedCasinoGameId = (
  selectedIds: ReadonlyArray<string>,
  game: Pick<CasinoGame, "game_id" | "match_ids">,
): string | undefined => {
  const matchIds = new Set(getCasinoGameMatchIds(game));
  return selectedIds.find((id) => matchIds.has(normalizeCasinoGameId(id)));
};

const capitalizeWord = (word: string): string =>
  word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word;

const CASINO_SLUG_SUFFIX_WORDS = [
  "royale",
  "fortune",
  "blackjack",
  "roulette",
  "candies",
  "families",
  "megaways",
  "bonanza",
  "treasure",
  "diamond",
  "wild",
  "gold",
];

/** Split concatenated slug tokens (`agentroyale` → `agent royale`). */
const splitEmbeddedSlugWords = (text: string): string => {
  const lower = text.toLowerCase();
  for (const suffix of CASINO_SLUG_SUFFIX_WORDS) {
    const idx = lower.indexOf(suffix);
    if (idx > 0) {
      return `${text.slice(0, idx)} ${text.slice(idx)}`;
    }
  }
  return text;
};

const titleCaseWords = (text: string): string =>
  text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map(capitalizeWord)
    .join(" ");

/** Best-effort readable label from provider slug ids (`101candiesr96f00` → `101 Candies`). */
export const humanizeCasinoGameSlug = (id: string): string => {
  const raw = normalizeCasinoGameId(id);
  if (!raw) return raw;

  let core = raw.replace(/0+$/, "");
  // Pragmatic-style RTP / hash tail (`…r96f00`, `…r96f`).
  core = core.replace(/r\d+[a-f0-9]*$/i, "");
  core = core.replace(/[a-f0-9]{4,}$/i, "");

  const digitPrefix = core.match(/^(\d+)([a-zA-Z].*)$/);
  if (digitPrefix) {
    const [, digits, rest] = digitPrefix;
    const words = titleCaseWords(splitEmbeddedSlugWords(rest));
    return words ? `${digits} ${words}` : digits;
  }

  const spaced = titleCaseWords(splitEmbeddedSlugWords(core));
  return spaced || raw;
};

/** Seed a game-id → display-name map from the BE-resolved `games[]` array on
 *  an admin task read (gamepass-api #371), so saved-task chips label without a
 *  catalogue join. Entries are best-effort — a catalogue miss ships
 *  `game_name: ""` — so blank names and names that merely echo the id are
 *  skipped, leaving those ids to the existing catalogue-lookup fallback.
 *  Returns `undefined` when nothing resolvable so callers can distinguish
 *  "no seed" from an empty selection. */
export const buildCasinoGameLabelsFromTaskGames = (
  games: ReadonlyArray<TaskResolvedGame> | null | undefined,
): Readonly<Record<string, string>> | undefined => {
  if (!Array.isArray(games) || games.length === 0) return undefined;

  const labels: Record<string, string> = {};
  for (const game of games) {
    const id = normalizeCasinoGameId(game?.game_id);
    const name = String(game?.game_name ?? "").trim();
    if (!id || !name || name === id) continue;
    labels[id] = name;
  }
  return Object.keys(labels).length > 0 ? labels : undefined;
};

/** Label shown on a selection chip — ignores stale maps that still store the raw id. */
export const resolveCasinoGameChipLabel = (
  gameId: string,
  labels: Readonly<Record<string, string>>,
): string => {
  const id = normalizeCasinoGameId(gameId);
  const stored = labels[id];
  if (stored && stored !== id) return stored;
  return humanizeCasinoGameSlug(id);
};

/** Pull a display name off a raw casino-games API row (field names vary by provider). */
export const extractCasinoGameNameFromApi = (
  row: Record<string, unknown>,
): string => {
  const candidates = [
    row.name,
    row.game_name,
    row.gameName,
    row.title,
    row.label,
    row.display_name,
    row.displayName,
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (value) return value;
  }
  return "";
};

/** Label for chips / picker rows — API name first, then slug humanisation. */
export const resolveCasinoGameLabel = (
  game: Pick<CasinoGame, "game_id" | "name">,
): string => {
  const gameId = normalizeCasinoGameId(game.game_id);
  const apiName = String(game.name ?? "").trim();
  if (apiName && apiName !== gameId) return apiName;

  const slugLabel = humanizeCasinoGameSlug(gameId);
  if (slugLabel && slugLabel !== gameId) return slugLabel;

  return apiName || gameId;
};

/** @deprecated Use {@link resolveCasinoGameLabel}. */
export const getCasinoGameDisplayName = resolveCasinoGameLabel;

/** Map a raw casino-games API row onto the picker wire shape. The Game Pass
 *  endpoint echoes several legacy field names (`id`, `game_name`, …). */
export const normalizeCasinoGameFromApi = (
  raw: unknown,
): CasinoGame | null => {
  if (!raw || typeof raw !== "object") return null;
  const matchIds = collectCasinoGameMatchIdsFromApi(raw);
  if (matchIds.length === 0) return null;

  const row = raw as Record<string, unknown>;
  const name = extractCasinoGameNameFromApi(row);

  return {
    game_id: matchIds[0],
    name,
    provider: String(row.provider ?? row.provider_id ?? row.sub ?? ""),
    category: String(row.category ?? row.subcategory ?? ""),
    is_active: row.is_active !== false,
    match_ids: matchIds,
  };
};
